import { useState } from "react";
import { useLocation } from "react-router-dom";
import { Input } from "@/shared/ui/input";
import { Label } from "@/shared/ui/label";
import { Button } from "@/shared/ui/button";
import { Alert, AlertDescription } from "@/shared/ui/alert";
import { useAuth } from "@/shared/hooks/useAuth";
import { useToast } from "@/shared/hooks/use-toast";
import { Loader2 } from "lucide-react";
import { firebaseAuth } from "@/shared/integrations/firebase/client";
import { trackEvent } from "@/shared/integrations/firebase/analytics";
import { signInWithEmailAndPassword, sendPasswordResetEmail } from "@firebase/auth";
import { getAppOriginUrl, withAppBasePath } from "@/shared/lib/appBasePath";

const FIREBASE_ERROR_MESSAGES: Record<string, string> = {
  "auth/invalid-credential": "Invalid email or password",
  "auth/wrong-password": "Invalid email or password",
  "auth/user-not-found": "No account found with this email",
  "auth/too-many-requests": "Too many attempts. Please try again later",
  "auth/invalid-email": "Please enter a valid email address",
  "auth/network-request-failed": "Network error. Please check your connection.",
};

function getFriendlyAuthError(error: unknown): string {
  if (error && typeof error === "object" && "code" in error && typeof (error as { code: string }).code === "string") {
    const code = (error as { code: string }).code;
    return FIREBASE_ERROR_MESSAGES[code] ?? "Something went wrong. Please try again.";
  }
  return "Something went wrong. Please try again.";
}

interface LoginFormProps {
  onClose?: () => void;
  variant?: "modal" | "fullscreen";
}

export function LoginForm({ onClose, variant = "modal" }: LoginFormProps) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetMode, setResetMode] = useState(false);
  const [resetSuccess, setResetSuccess] = useState(false);
  const { redirectToUserDashboard } = useAuth();
  const { toast } = useToast();
  const location = useLocation();

  const showError = (message: string, title?: string) => {
    toast({
      title: title || "Sign in failed",
      description: message,
      variant: "destructive",
    });
  };

  const handleLogin = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!firebaseAuth) {
        throw new Error("Firebase is not configured. Please check your environment variables.");
      }

      if (!email || !password) {
        throw new Error("Email and password are required");
      }

      await signInWithEmailAndPassword(firebaseAuth, email, password);
      trackEvent("login", { method: "password" });

      if (variant === "modal" && onClose) {
        onClose();
      }

      setTimeout(() => {
        // Honor the destination ProtectedRoute saved (e.g. a shared dashboard
        // link `/dashboard?d=…`) so the ?d= share payload
        // survives the login round-trip; else fall back to the role default.
        const from = (
          location.state as { from?: { pathname?: string; search?: string } } | null
        )?.from;
        if (from?.pathname) {
          window.location.href = withAppBasePath(`${from.pathname}${from.search ?? ""}`);
        } else {
          redirectToUserDashboard();
        }
      }, 500);
    } catch (error) {
      console.error("Firebase Login Error:", error);
      showError(getFriendlyAuthError(error));
    } finally {
      setLoading(false);
    }
  };

  const handlePasswordReset = async (e: React.FormEvent) => {
    e.preventDefault();
    setLoading(true);

    try {
      if (!firebaseAuth) {
        throw new Error("Firebase is not configured");
      }

      await sendPasswordResetEmail(firebaseAuth, email, {
        url: `${getAppOriginUrl()}/reset-password`,
      });

      setResetSuccess(true);
    } catch (error) {
      console.error("Firebase Password Reset Error:", error);
      showError(getFriendlyAuthError(error), "Password reset failed");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setEmail("");
    setPassword("");
    setResetMode(false);
    setResetSuccess(false);
    setLoading(false);
  };

  const handleClose = () => {
    resetForm();
    onClose?.();
  };

  return (
    <>
      {resetSuccess ? (
        <div className="space-y-4">
          <Alert>
            <AlertDescription>Password reset link has been sent to your email address.</AlertDescription>
          </Alert>
          <Button onClick={handleClose} className="w-full" variant="default">
            Close
          </Button>
        </div>
      ) : (
        <form onSubmit={resetMode ? handlePasswordReset : handleLogin} className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground">
              Email
            </Label>
            <Input
              id="email"
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="Enter your email"
              required
              className="bg-background border-border text-foreground"
            />
          </div>

          {!resetMode && (
            <div className="space-y-2">
              <Label htmlFor="password" className="text-foreground">
                Password
              </Label>
              <Input
                id="password"
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                placeholder="Enter your password"
                required
                className="bg-background border-border text-foreground"
              />
            </div>
          )}

          <Button
            type="submit"
            disabled={loading}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground"
          >
            {loading && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
            {resetMode ? "Send Reset Link" : "Sign In"}
          </Button>

          <div className="text-center space-y-2">
            {!resetMode ? (
              <button
                type="button"
                onClick={() => setResetMode(true)}
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                Forgot your password?
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setResetMode(false)}
                className="text-sm text-muted-foreground hover:text-foreground underline"
              >
                Back to Sign In
              </button>
            )}
          </div>
        </form>
      )}
    </>
  );
}
